import * as React from 'react';
import {
  Badge,
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  Flex,
  MenuToggle,
  MenuToggleElement,
  PageSection,
  SearchInput,
  Select,
  SelectOption,
  Stack,
  StackItem,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Pagination,
} from '@patternfly/react-core';
import { PageContextActions, PageHeaderTitle } from '@app/components/PageContextActions';
import { StatusLabel } from '@app/components/StatusLabel';
import { EllipsisVIcon } from '@patternfly/react-icons';
import { useDocumentTitle } from '@app/utils/useDocumentTitle';

interface AuthenticationProvider {
  name: string;
  displayName: string;
  type: string;
  issuerUrl: string;
  status: 'Enabled' | 'Disabled';
}

const Settings: React.FunctionComponent = () => {
  useDocumentTitle('Authentications');
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [selectedFilter, setSelectedFilter] = React.useState('All providers');
  const [openDropdowns, setOpenDropdowns] = React.useState<Record<string, boolean>>({});
  const [searchValue, setSearchValue] = React.useState('');

  // Pagination state
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(10);

  const allAuthenticationProviders: AuthenticationProvider[] = [
    {
      name: 'enterprise-platform',
      displayName: 'Enterprise Platform SSO',
      type: 'OAuth2',
      issuerUrl: 'https://aap.example.com/api/gateway/v1/social/',
      status: 'Enabled',
    },
    {
      name: 'google',
      displayName: 'Google Workspace',
      type: 'OIDC',
      issuerUrl: 'https://accounts.google.com',
      status: 'Enabled',
    },
    {
      name: 'customer-b-okta',
      displayName: 'Customer B Okta',
      type: 'OIDC',
      issuerUrl: 'https://customer-b.okta.com/oauth2/default',
      status: 'Disabled',
    },
    {
      name: 'k8s-cluster-auth',
      displayName: 'Kubernetes Cluster Auth',
      type: 'OIDC',
      issuerUrl: 'https://k8s.cluster.local:6443',
      status: 'Enabled',
    },
  ];

  const onFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const onFilterSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => {
    setSelectedFilter(value as string);
    setIsFilterOpen(false);
  };

  const filterToggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={onFilterToggle}
      isExpanded={isFilterOpen}
      style={{ width: '100%', maxWidth: '200px' }}
    >
      {selectedFilter}
    </MenuToggle>
  );

  // Filter providers based on search
  const filteredProviders = React.useMemo(() => {
    if (!searchValue) {
      return allAuthenticationProviders;
    }
    return allAuthenticationProviders.filter(provider =>
      provider.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      provider.displayName.toLowerCase().includes(searchValue.toLowerCase()) ||
      provider.type.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [searchValue]);

  // Pagination logic
  const startIndex = (page - 1) * perPage;
  const endIndex = startIndex + perPage;
  const paginatedProviders = filteredProviders.slice(startIndex, endIndex);

  const onSetPage = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPage: number) => {
    setPage(newPage);
  };

  const onPerPageSelect = (_event: React.MouseEvent | React.KeyboardEvent | MouseEvent, newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  React.useEffect(() => {
    setPage(1); // Reset to first page when searching
  }, [searchValue]);

  const onActionDropdownToggle = (providerName: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [providerName]: !prev[providerName]
    }));
  };

  const onActionDropdownSelect = (providerName: string) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [providerName]: false
    }));
  };

  const handleViewDetails = (providerName: string) => {
    console.log('View details for:', providerName);
    onActionDropdownSelect(providerName);
  };

  const handleEdit = (providerName: string) => {
    console.log('Edit:', providerName);
    onActionDropdownSelect(providerName);
  };

  const handleDelete = (providerName: string) => {
    console.log('Delete:', providerName);
    onActionDropdownSelect(providerName);
  };

  return (
    <>
      {/* Page Header with Toolbar - Single Line */}
      <PageSection hasBodyWrapper={false} style={{ paddingBottom: 0 }}>
        <div style={{ paddingLeft: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
        <Flex 
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsCenter' }}
            style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}
        >
          <PageHeaderTitle
            title="Authentications"
            description="Manage authentication providers, security policies, and access controls."
          />
          <PageContextActions placement="toolbar" />
        </Flex>
        </div>
      </PageSection>

      {/* Main Content */}
      <PageSection hasBodyWrapper={false} style={{ paddingLeft: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))', paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
        <Stack hasGutter>
          <StackItem>
            <Toolbar>
              <ToolbarContent>
                <ToolbarItem>
                  <Select
                    toggle={filterToggle}
                    onSelect={onFilterSelect}
                    selected={selectedFilter}
                    isOpen={isFilterOpen}
                    onOpenChange={(isOpen) => setIsFilterOpen(isOpen)}
                  >
                    <SelectOption value="All providers">All providers</SelectOption>
                    <SelectOption value="Enabled">Enabled</SelectOption>
                    <SelectOption value="Disabled">Disabled</SelectOption>
                    <SelectOption value="OAuth2">OAuth2</SelectOption>
                    <SelectOption value="OIDC">OIDC</SelectOption>
                  </Select>
                </ToolbarItem>
                <ToolbarItem>
                  <SearchInput
                    className="app-search-input"
                    placeholder="Search"
                    value={searchValue}
                    onChange={(_, value) => setSearchValue(value)}
                    onClear={() => setSearchValue('')}
                  />
                </ToolbarItem>
                <ToolbarItem>
                  <Button variant="primary">
                    Add Authentication Provider
                  </Button>
                </ToolbarItem>
              </ToolbarContent>
            </Toolbar>
          </StackItem>
          <StackItem>

        <div className="app-data-table">
          {/* Table Header */}
          <div className="app-data-table__header" style={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 2fr 1fr 2.5fr 1fr 0.5fr',
            padding: 'var(--pf-t--global--spacer--sm) var(--pf-t--global--spacer--md)',
            backgroundColor: 'var(--pf-t--global--color--nonstatus--gray--50)',
            borderBottom: '1px solid var(--pf-t--global--color--nonstatus--gray--200)',
            fontWeight: 'bold',
            fontSize: 'var(--pf-t--global--font--size--sm)',
          }}>
            <div>Provider name</div>
            <div>Display Name</div>
            <div>Type</div>
            <div>Issuer URL</div>
            <div>Status</div>
            <div></div>
          </div>

          {/* Table Rows */}
          {paginatedProviders.map((provider, index) => (
            <div
              key={provider.name}
              style={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 2fr 1fr 2.5fr 1fr 0.5fr',
                padding: 'var(--pf-t--global--spacer--sm) var(--pf-t--global--spacer--md)',
                borderBottom: index < paginatedProviders.length - 1 
                  ? '1px solid var(--pf-t--global--color--nonstatus--gray--200)' 
                  : 'none',
                alignItems: 'center',
              }}
            >
              <div>
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  style={{
                    color: 'var(--pf-t--global--color--brand--default)',
                    textDecoration: 'none',
                  }}
                >
                  {provider.name}
                </a>
              </div>
              <div>{provider.displayName}</div>
              <div>
                <StatusLabel status="info">
                  {provider.type}
                </StatusLabel>
              </div>
              <div style={{ wordBreak: 'break-word' }}>{provider.issuerUrl}</div>
              <div>
                <StatusLabel
                  status={provider.status === 'Enabled' ? 'success' : 'unknown'}
                >
                  {provider.status}
                </StatusLabel>
              </div>
              <div>
                <Dropdown
                  isOpen={openDropdowns[provider.name] || false}
                  onSelect={() => onActionDropdownSelect(provider.name)}
                  onOpenChange={(isOpen: boolean) => {
                    setOpenDropdowns(prev => ({
                      ...prev,
                      [provider.name]: isOpen
                    }));
                  }}
                  toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                    <MenuToggle
                      ref={toggleRef}
                      onClick={() => onActionDropdownToggle(provider.name)}
                      isExpanded={openDropdowns[provider.name] || false}
                      variant="plain"
                      icon={<EllipsisVIcon />}
                      aria-label={`Actions for ${provider.name}`}
                    />
                  )}
                >
                  <DropdownList>
                    <DropdownItem
                      key="view-details"
                      onClick={() => handleViewDetails(provider.name)}
                    >
                      View
                    </DropdownItem>
                    <DropdownItem
                      key="edit"
                      onClick={() => handleEdit(provider.name)}
                      isDisabled
                    >
                      Edit
                    </DropdownItem>
                    <DropdownItem
                      key="delete"
                      onClick={() => handleDelete(provider.name)}
                    >
                      Delete
                    </DropdownItem>
                  </DropdownList>
                </Dropdown>
              </div>
            </div>
          ))}
        </div>
          </StackItem>
          <StackItem>
            <div style={{ paddingRight: 'var(--acm-page-inline-x, var(--pf-t--global--spacer--md))' }}>
              <Pagination
                itemCount={filteredProviders.length}
                perPage={perPage}
                page={page}
                onSetPage={onSetPage}
                widgetId="settings-pagination"
                onPerPageSelect={onPerPageSelect}
                isCompact
              />
        </div>
          </StackItem>
        </Stack>
      </PageSection>
    </>
  );
};

export { Settings };

